import { FC, ReactNode } from 'react';
import { Control, Controller, FieldErrors, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Svg, Text } from '@hopskipdrive/react-components';
import { CheckRounded as CheckRoundedIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjsPluginTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { capitalize } from 'lodash';

import { Calendar, CheckeredFlagIcon } from '../../../../../shared/components/Icons';
import { colorPalette, colorTokens } from '../../../../../shared/components/variables';
import { formatInTimezone } from '../../../../../shared/utils/helpers/date';
import { OutlinedCircle } from '../../../../components/OutlineCircleNumber';
import { RIDE_STATES } from '../../../../constants/rideStates';
import { RideOverviewDetails, RouteDetail } from '../../../../types/rideOverview';
import { TimestampsFormType } from '../../../../types/timestamps';
import { LocationInnerCard } from '../LocationInnerCard';
import { helpers, RidersList } from '../TimelineOverview/TimelineOverview';
import { FilledCircle } from '../TimelineOverview/TimelineOverview.styles';

import { TimestampDateTimePicker } from './EditTimestampModal.styles';
import { editTimestampValidationSchema } from './editTimestampValidationSchema';

interface EditTimestampFormProps {
  rideOverviewDetails: RideOverviewDetails;
  onSubmit: (data: TimestampsFormType) => void;
}

dayjs.extend(utc);
dayjs.extend(dayjsPluginTimezone);
dayjs.extend(localizedFormat);

const plannedDateFormat = "EEE, M/d/yyyy 'at' h:mm a zzz";

export const EditTimestampForm: React.FC<EditTimestampFormProps> = ({ rideOverviewDetails, onSubmit }) => {
  const { checkInDetails, routeDetails, actualStartsAt, actualEndsAt, timeAnchor } = rideOverviewDetails;

  const routeDetailsLength = routeDetails?.length || 0;
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TimestampsFormType>({
    defaultValues: {
      actualCheckedInAt: rideOverviewDetails.checkInDetails.actualCheckedInAt || '',
      actualStartsAt: rideOverviewDetails.actualStartsAt || '',
      actualEndsAt: rideOverviewDetails.actualEndsAt || '',
      endsAt: rideOverviewDetails.endsAt || '',
      startsAt: rideOverviewDetails.startsAt || '',
      leaveAt: rideOverviewDetails.leaveAt || '',
      waypointsAttributes: routeDetails?.map((routeDetail) => ({
        id: routeDetail?.waypointId,
        actualArrivesAt: routeDetail?.arrivalDetails.actual || '',
        actualDepartsAt: routeDetail?.departureDetails.actual || '',
      })),
    },
    resolver: yupResolver(editTimestampValidationSchema()),
  });

  const {} = useFieldArray({
    control,
    name: 'waypointsAttributes',
  });

  const plannedCheckIn = `${formatInTimezone(
    checkInDetails?.plannedStartTime,
    rideOverviewDetails.timeZone,
    'EEE, M/d/yyyy h:mm a'
  )} - ${formatInTimezone(checkInDetails?.plannedEndTime, rideOverviewDetails.timeZone, 'h:mm a zzz')}`;

  return (
    <form id="edit-timestamp-form" onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" gap="22px" flexDirection="column" alignItems="flex-start">
        <EditTimestampItem
          dotContent={
            checkInDetails?.actualCheckedInAt ? (
              <FilledCircle $isCompleted>
                <CheckRoundedIcon
                  style={{
                    width: '20px',
                    height: '20px',
                  }}
                />
              </FilledCircle>
            ) : (
              <OutlinedCircle />
            )
          }
          name={'actualCheckedInAt'}
          control={control}
          errors={errors}
          plannedTimestamp={plannedCheckIn}
          actualTimestamp={checkInDetails?.actualCheckedInAt}
          timeZone={rideOverviewDetails.timeZone}
        >
          <Text variant="h6">Check in</Text>
        </EditTimestampItem>
        <EditTimestampItem
          dotContent={
            actualStartsAt ? (
              <FilledCircle $isCompleted>
                <CheckRoundedIcon
                  style={{
                    width: '20px',
                    height: '20px',
                  }}
                />
              </FilledCircle>
            ) : (
              <OutlinedCircle />
            )
          }
          name={'actualStartsAt'}
          control={control}
          errors={errors}
          plannedTimestamp={formatInTimezone(
            rideOverviewDetails.leaveAt,
            rideOverviewDetails.timeZone,
            plannedDateFormat
          )}
          actualTimestamp={rideOverviewDetails.actualStartsAt}
          timeZone={rideOverviewDetails.timeZone}
        >
          <Text variant="h6">Left at</Text>
          {RIDE_STATES.inProgress.includes(rideOverviewDetails.state) && (
            <Text variant="body1" textColor="secondaryText" fontWeight={400}>
              Ride in progress
            </Text>
          )}
        </EditTimestampItem>
        {routeDetails?.map((routeDetail, index) => (
          <Box display="flex" flexDirection="column" gap="22px" key={index}>
            <EditTimestampRouteItem
              dotContent={
                <Box display="flex" flexDirection="row" gap="8px" alignItems="center">
                  <Box>
                    {routeDetail.arrivalDetails.actual ? (
                      <FilledCircle $isCompleted>{index + 1}</FilledCircle>
                    ) : (
                      <OutlinedCircle>{index + 1}</OutlinedCircle>
                    )}
                  </Box>
                  <Box>
                    <Text variant="h6">
                      {capitalize(helpers[timeAnchor].getWaypointTitle(index, routeDetailsLength))}
                    </Text>
                  </Box>
                </Box>
              }
              control={control}
              errors={errors}
              routeDetail={routeDetail}
              timeZone={rideOverviewDetails.timeZone}
              routeDetailsLength={routeDetailsLength}
              index={index}
            >
              <RidersList isFetching={false} riders={routeDetail?.riders} />
              <LocationInnerCard locationDetails={routeDetail?.locationDetails} excludeInstructions={true} />
            </EditTimestampRouteItem>
          </Box>
        ))}
        <EditTimestampItem
          dotContent={
            actualEndsAt ? (
              <FilledCircle $isCompleted={true}>
                <CheckeredFlagIcon />
              </FilledCircle>
            ) : (
              <OutlinedCircle>
                <CheckeredFlagIcon style={{ color: colorTokens.text.disabled }} />
              </OutlinedCircle>
            )
          }
          name={'actualEndsAt'}
          control={control}
          errors={errors}
          plannedTimestamp={formatInTimezone(
            rideOverviewDetails.endsAt,
            rideOverviewDetails.timeZone,
            plannedDateFormat
          )}
          actualTimestamp={rideOverviewDetails.actualEndsAt}
          timeZone={rideOverviewDetails.timeZone}
        >
          <Text variant="h6">Ride complete</Text>
        </EditTimestampItem>
      </Box>
    </form>
  );
};

interface EditTimestampTimelineItemProps {
  dotContent?: ReactNode;
  children: ReactNode;
  name: string;
  control: Control<TimestampsFormType>;
  errors: FieldErrors<TimestampsFormType>;
  plannedTimestamp?: string | Nullable<Date>;
  actualTimestamp?: Nullable<Date>;
  timeZone: string;
}

const EditTimestampItem: FC<EditTimestampTimelineItemProps> = ({
  dotContent,
  name,
  control,
  plannedTimestamp,
  actualTimestamp,
  timeZone,
  children,
}) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={3} sx={{ width: '100%' }}>
      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        <Box>{dotContent}</Box>
        <Box>{children}</Box>
      </Box>
      <EditTimestampFormField
        name={name}
        control={control}
        plannedTimestamp={plannedTimestamp}
        actualTimestamp={actualTimestamp}
        timeZone={timeZone}
      />
    </Box>
  );
};

interface EditTimestampRouteItemProps {
  dotContent: ReactNode;
  control: Control<TimestampsFormType>;
  errors: FieldErrors<TimestampsFormType>;
  routeDetail: RouteDetail;
  timeZone: string;
  routeDetailsLength: number;
  index: number;
  children: ReactNode;
}

const EditTimestampRouteItem: FC<EditTimestampRouteItemProps> = ({
  dotContent,
  control,
  routeDetail,
  timeZone,
  routeDetailsLength,
  index,
  children,
}) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={3} sx={{ width: '100%' }}>
      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        <Box>{dotContent}</Box>
      </Box>
      <Box>{children}</Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2} sx={{ width: '100%' }}>
        <Box display="flex" flexDirection="row" alignItems="center" gap="9px">
          <Svg icon="enter" />
          <Text variant="h6">Arrival</Text>
        </Box>
        <Box>
          <EditTimestampFormField
            name={`waypointsAttributes[${index}].actualArrivesAt`}
            control={control}
            plannedTimestamp={formatInTimezone(routeDetail.arrivalDetails.planned, timeZone, plannedDateFormat)}
            actualTimestamp={routeDetail.arrivalDetails.actual}
            timeZone={timeZone}
          />
        </Box>
        {routeDetailsLength - 1 !== index && (
          <>
            <Box display="flex" flexDirection="row" alignItems="center" gap="9px">
              <Svg icon="enter" />
              <Text variant="h6">Departure</Text>
            </Box>
            <Box>
              <EditTimestampFormField
                name={`waypointsAttributes[${index}].actualDepartsAt`}
                control={control}
                plannedTimestamp={formatInTimezone(routeDetail.departureDetails.planned, timeZone, plannedDateFormat)}
                actualTimestamp={routeDetail.departureDetails.actual}
                timeZone={timeZone}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

const EditTimestampFormField = ({ name, control, plannedTimestamp, actualTimestamp, timeZone }) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
      <EditTimestampInputField label="Planned" timestamp={plannedTimestamp} name={name} />
      <EditTimestampInputField
        label="Actual"
        timestamp={actualTimestamp}
        timeZone={timeZone}
        name={name}
        control={control}
      />
    </Box>
  );
};

const EditTimestampInputField = ({ label, timestamp, timeZone = undefined, name, control = undefined }) => {
  const dateFormat = 'l LT';

  dayjs.tz.setDefault(timeZone);

  return (
    <Box display="flex" alignItems="center" gap={6} sx={{ width: '100%' }}>
      <Box width="100%">
        {label === 'Planned' ? (
          <Box display="flex" data-testid={`${label}-${name}`} alignItems="center" justifyContent="space-between">
            <Box display="flex" gap="80px">
              <Text variant="body2" textColor="secondaryText">
                {label}
              </Text>
              <Box display="flex" gap="16px">
                <Calendar width="27.42px" height="27.42px" />
                <Text variant="body2" textColor="secondaryText">
                  {timestamp}
                </Text>
              </Box>
            </Box>
          </Box>
        ) : (
          <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box display="flex" data-testid={`${label}-${name}`} alignItems="center" justifyContent="space-between">
                <Box width="124px" style={{ marginTop: '-17px' }}>
                  <Text variant="body2" textColor="secondaryText">
                    {label}
                  </Text>
                </Box>
                <Box sx={{ position: 'relative' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimestampDateTimePicker
                      onChange={onChange}
                      timeSteps={{ minutes: 1 }}
                      value={value ? dayjs(value).tz(timeZone) : null}
                      format={dateFormat}
                      slots={{
                        openPickerIcon: Calendar,
                      }}
                      slotProps={{
                        textField: {
                          placeholder: 'Enter Date and Time',
                          error: !!error,
                          helperText: error?.message || ' ',
                        },
                        field: { shouldRespectLeadingZeros: true },
                        inputAdornment: {
                          position: 'start',
                        },
                        actionBar: {
                          actions: ['clear', 'today'],
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 10,
                      top: 20,
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                    }}
                  >
                    <Svg
                      icon="cancel-filled"
                      onClick={() => {
                        onChange(null);
                      }}
                      style={{ color: colorPalette.neutral400 }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          />
        )}
      </Box>
    </Box>
  );
};
